import Link from 'next/link';
import Container from '@/components/Container';
import spiritoryStrapiApi from '@/lib/axios/spiritoryStrapiApi';
import { createGetServerSideProps } from '@/lib/serverSideProps/createDefaultServerSideProps';
import { createBrandUrl } from '@/utilites/search/create-search-urls';
import getServerSideTranslations from '@/utilites/translations/get-server-side-translations.translations.utils';
import { StrapiBrand } from '@/types/strapi';

type BrandIndexPageProps = {
  brandGroups: {
    letter: string;
    brands: {
      name: string;
      href: string;
    }[];
  }[];
};

const BRAND_GROUP_FALLBACK = '#';

const getBrandGroupLetter = (brandName: string): string => {
  const normalizedName = brandName.trim();

  if (!normalizedName) {
    return BRAND_GROUP_FALLBACK;
  }

  const firstCharacter = normalizedName.charAt(0).toUpperCase();

  return /[A-Z]/.test(firstCharacter) ? firstCharacter : BRAND_GROUP_FALLBACK;
};

export default function BrandsIndexPage({ brandGroups }: BrandIndexPageProps): JSX.Element {
  return (
    <div className="tw-bg-white tw-py-8 tw-text-text md:tw-py-12">
      <Container>
        <div className="tw-mx-auto tw-max-w-7xl">
          <div className="tw-mb-8 lg:tw-hidden">
            <h1 className="tw-mb-5 tw-font-plusJakartaSans tw-text-3xl tw-font-semibold">
              All Brands
            </h1>
            <nav aria-label="Brand index" className="tw-flex tw-flex-wrap tw-gap-x-3 tw-gap-y-2">
              {brandGroups.map(({ letter }) => (
                <a
                  key={letter}
                  href={`#brands-${letter}`}
                  className="tw-font-plusJakartaSans tw-text-sm tw-font-medium tw-text-text tw-underline tw-underline-offset-2 hover:tw-text-main"
                >
                  {letter}
                </a>
              ))}
            </nav>
          </div>

          <div className="lg:tw-grid lg:tw-grid-cols-[220px_minmax(0,1fr)] lg:tw-gap-16 xl:tw-grid-cols-[240px_minmax(0,1fr)]">
            <aside className="tw-hidden lg:tw-block">
              <div className="tw-sticky tw-top-24">
                <h1 className="tw-mb-5 tw-font-plusJakartaSans tw-text-[28px] tw-font-semibold">
                  All Brands
                </h1>
                <nav
                  aria-label="Brand index"
                  className="tw-grid tw-grid-cols-10 tw-gap-x-3 tw-gap-y-3 xl:tw-grid-cols-5"
                >
                  {brandGroups.map(({ letter }) => (
                    <a
                      key={letter}
                      href={`#brands-${letter}`}
                      className="tw-font-plusJakartaSans tw-text-[15px] tw-font-medium tw-text-text tw-underline tw-underline-offset-2 hover:tw-text-main"
                    >
                      {letter}
                    </a>
                  ))}
                </nav>
              </div>
            </aside>

            <div className="tw-space-y-10 md:tw-space-y-12">
              {brandGroups.map(({ letter, brands }) => (
                <section key={letter} id={`brands-${letter}`} className="tw-scroll-mt-24">
                  <h2 className="tw-mb-4 tw-font-plusJakartaSans tw-text-[30px] tw-font-medium">
                    {letter}
                  </h2>

                  <div className="tw-grid tw-grid-cols-1 tw-gap-x-8 tw-gap-y-3 sm:tw-grid-cols-2 xl:tw-grid-cols-4">
                    {brands.map(brand => (
                      <Link
                        key={brand.href}
                        href={brand.href}
                        className="tw-w-fit tw-font-plusJakartaSans tw-text-[15px] tw-font-medium tw-text-text tw-no-underline hover:tw-text-main hover:tw-underline"
                      >
                        {brand.name}
                      </Link>
                    ))}
                  </div>
                </section>
              ))}
            </div>
          </div>
        </div>
      </Container>
    </div>
  );
}

export const getServerSideProps = createGetServerSideProps(async context => {
  const locale = context.locale || 'en';

  const [translationsProps, brands] = await Promise.all([
    getServerSideTranslations(context, ['common', 'index', 'blog', 'sku', 'skus', 'index-v2']),
    spiritoryStrapiApi.getBrands(`locale=${locale}&pagination[limit]=1000&sort[0]=name:asc`),
  ]);

  const collator = new Intl.Collator(locale, {
    sensitivity: 'base',
    numeric: true,
  });

  const uniqueBrands = Array.from(
    new Map(
      (brands || [])
        .filter((brand: any) => Boolean(brand?.name?.trim && brand.name.trim()))
        .map((brand: any) => {
          const rawName = typeof brand.name === 'string' ? brand.name : '';
          const name = rawName.trim();
          return [
            name.toLocaleLowerCase(locale),
            {
              name,
              href: createBrandUrl(name),
            },
          ];
        })
    ).values()
  ).sort((left, right) => collator.compare(left.name, right.name));

  const brandGroups = uniqueBrands.reduce<BrandIndexPageProps['brandGroups']>((groups, brand) => {
    const letter = getBrandGroupLetter(brand.name);
    const existingGroup = groups.find(group => group.letter === letter);

    if (existingGroup) {
      existingGroup.brands.push(brand);
      return groups;
    }

    groups.push({
      letter,
      brands: [brand],
    });

    return groups;
  }, []);

  return {
    props: {
      ...translationsProps,
      brandGroups,
    },
  };
});
